import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Calendar, Clock, ArrowRight, Tag } from 'lucide-react';
import { blogPosts as mockPosts, blogCategories } from '../blogData';
import { fetchBlogs } from '../api';
import SafeImage from '../components/SafeImage';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useSettings } from '../store';
import { applyPageSEO } from '../utils/pageSEO';

const fmtDate = (d) => {
  const dt = new Date(d);
  return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const BlogList = () => {
  const [cat, setCat] = useState('All');
  const [q, setQ] = useState('');
  const [posts, setPosts] = useState(mockPosts);
  const settings = useSettings();

  useEffect(() => {
    return applyPageSEO({
      title: settings.blog_list_seo_title || 'Guides & Stories',
      description: settings.blog_list_seo_description || 'Travel guides, itineraries and stories from Travelsera.',
      noIndex: !!settings.blog_list_seo_no_index,
      noFollow: !!settings.blog_list_seo_no_follow,
    });
  }, [settings.blog_list_seo_title, settings.blog_list_seo_description, settings.blog_list_seo_no_index, settings.blog_list_seo_no_follow]);

  useEffect(() => {
    fetchBlogs()
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) setPosts(data);
      })
      .catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return posts.filter((p) => {
      if (cat !== 'All' && p.category !== cat) return false;
      if (!term) return true;
      const hay = (p.title + ' ' + p.excerpt + ' ' + (p.tags || []).join(' ')).toLowerCase();
      return hay.includes(term);
    });
  }, [cat, q, posts]);

  const featured = filtered[0];
  const rest = filtered.slice(1);

  return (
    <div className="min-h-screen bg-white">
      <div className="relative bg-[#0d64a8]">
        <Header />
        <div className="max-w-[1280px] mx-auto px-5 lg:px-8 pt-24 pb-14 text-white">
          <div className="text-xs uppercase tracking-widest opacity-80 mb-2">Travel Journal</div>
          <h1 className="text-3xl md:text-4xl font-bold">Guides, Stories & Trip Ideas</h1>
          <p className="text-white/85 mt-2 max-w-2xl">
            Real advice from travellers who have been there. Plan smarter, pack lighter and skip the tourist traps.
          </p>

          <div className="mt-6 flex flex-col md:flex-row gap-3 max-w-2xl">
            <div className="flex-1 flex items-center gap-2 bg-white rounded-md px-3 py-2.5">
              <Search size={16} className="text-[#0d64a8]" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search articles — e.g. Bali, Ladakh, Family"
                className="flex-1 outline-none text-sm text-gray-800 placeholder:text-gray-400"
              />
            </div>
          </div>
        </div>
      </div>

      <section className="py-8">
        <div className="max-w-[1280px] mx-auto px-5 lg:px-8">
          <div className="flex flex-wrap gap-2 mb-8">
            {blogCategories.map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                className={`text-sm px-4 py-1.5 rounded-full border transition ${
                  cat === c
                    ? 'bg-[#0d64a8] text-white border-[#0d64a8]'
                    : 'bg-white text-gray-700 border-gray-200 hover:border-[#0d64a8] hover:text-[#0d64a8]'
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="py-16 text-center">
              <p className="text-gray-600">No articles match your filter yet.</p>
            </div>
          )}

          {featured && (
            <Link
              to={`/blog/${featured.slug}`}
              className="group grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10 bg-white border border-gray-100 rounded-2xl overflow-hidden hover:shadow-xl transition-all"
            >
              <div className="relative h-72 lg:h-full overflow-hidden">
                <SafeImage
                  src={featured.hero}
                  alt={featured.title}
                  fallbackType={featured.fbType}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <span className="absolute top-4 left-4 bg-[#ffcc29] text-[#0a2a4a] text-[11px] font-semibold px-2.5 py-1 rounded">FEATURED</span>
              </div>
              <div className="p-6 lg:p-8 flex flex-col justify-center">
                <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                  <span className="bg-[#e8f2fb] text-[#0d64a8] px-2 py-0.5 rounded font-medium">{featured.category}</span>
                  <span className="flex items-center gap-1"><Calendar size={11}/> {fmtDate(featured.date)}</span>
                  <span className="flex items-center gap-1"><Clock size={11}/> {featured.readTime}</span>
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 group-hover:text-[#0d64a8] transition">
                  {featured.title}
                </h2>
                <p className="text-gray-600 mt-3 leading-relaxed">{featured.excerpt}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-[#0d64a8] font-medium text-sm">
                  Read the guide <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform"/>
                </span>
              </div>
            </Link>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {rest.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                className="group bg-white rounded-xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
              >
                <div className="relative h-52 overflow-hidden">
                  <SafeImage
                    src={p.hero}
                    alt={p.title}
                    fallbackType={p.fbType}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <span className="absolute top-3 left-3 bg-white/95 backdrop-blur text-[#0d64a8] text-[11px] font-semibold px-2 py-0.5 rounded">
                    {p.category}
                  </span>
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 text-[11px] text-gray-500 mb-2">
                    <span className="flex items-center gap-1"><Calendar size={11}/> {fmtDate(p.date)}</span>
                    <span>•</span>
                    <span className="flex items-center gap-1"><Clock size={11}/> {p.readTime}</span>
                  </div>
                  <h3 className="font-semibold text-gray-900 text-[17px] leading-snug group-hover:text-[#0d64a8] transition line-clamp-2 min-h-[46px]">
                    {p.title}
                  </h3>
                  <p className="text-sm text-gray-600 mt-2 line-clamp-3">{p.excerpt}</p>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-xs text-gray-500">by {p.author}</span>
                    <span className="text-[#0d64a8] text-sm font-medium inline-flex items-center gap-1">
                      Read <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform"/>
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogList;
